import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Box, Button, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import {
  marketingColor,
  marketingFont,
  marketingLine,
  marketingSurface,
} from '../../theme/marketingTokens';

/**
 * Public API health endpoint. `REACT_APP_HEALTH_URL` is already set by the
 * `build:prod` script; the fallback keeps the check working for builds that
 * do not set it.
 */
const HEALTH_URL = process.env.REACT_APP_HEALTH_URL || 'https://api.motzulabs.com/health';

const TIMEOUT_MS = 8000;

type Phase = 'checking' | 'operational' | 'unreachable';

interface Result {
  phase: Phase;
  /** When the check completed, for the "checked at" line. */
  checkedAt?: Date;
  /** Version the endpoint reports, when it responds. */
}

/**
 * Live status of the public API.
 *
 * This reports exactly what it measures: whether `/health` answers. That
 * endpoint is a liveness probe — it returns `healthy` whenever the process is
 * serving and does not probe Firestore, billing or the integrations — so the
 * copy claims the API responded, not that every system is green.
 */
export const ApiStatus: React.FC = () => {
  const { t } = useTranslation() as { t: (key: string, options?: any) => any };
  const [result, setResult] = useState<Result>({ phase: 'checking' });
  const alive = useRef(true);
  const inFlight = useRef<AbortController | null>(null);

  const check = useCallback(async () => {
    // Abort whatever is still running, so a slow first check can't resolve
    // after a newer one and leave a stale phase on screen.
    inFlight.current?.abort();
    setResult({ phase: 'checking' });
    const controller = new AbortController();
    inFlight.current = controller;
    const timer = setTimeout(() => controller.abort(), TIMEOUT_MS);
    try {
      const response = await fetch(HEALTH_URL, {
        signal: controller.signal,
        cache: 'no-store',
      });
      const body = response.ok ? await response.json().catch(() => null) : null;
      if (!alive.current || controller.signal.aborted) return;
      setResult({
        phase: body?.status === 'healthy' ? 'operational' : 'unreachable',
        checkedAt: new Date(),
      });
    } catch {
      // Network failure, timeout, or an unparseable body — all mean the same
      // thing to a reader: we could not confirm the API is up. An abort from a
      // newer check is not a result, so it must not paint one.
      if (!alive.current || controller.signal.aborted) return;
      setResult({ phase: 'unreachable', checkedAt: new Date() });
    } finally {
      clearTimeout(timer);
      if (inFlight.current === controller) inFlight.current = null;
    }
  }, []);

  useEffect(() => {
    alive.current = true;
    check();
    return () => {
      alive.current = false;
      inFlight.current?.abort();
    };
  }, [check]);

  const { phase, checkedAt } = result;
  const tone =
    phase === 'operational'
      ? marketingColor.accent
      : phase === 'unreachable'
        ? marketingColor.error
        : marketingColor.textMuted;

  return (
    <Box sx={{ textAlign: 'center' }}>
      <Typography variant="h3" sx={{ fontSize: { xs: '1.65rem', md: '1.875rem' }, mb: 1.5 }}>
        {t('public.contact.apiStatus.title')}
      </Typography>
      <Typography sx={{ fontSize: 14.5, color: marketingColor.textSecondary, mb: 3.5 }}>
        {t('public.contact.apiStatus.subtitle')}
      </Typography>

      <Box
        role="status"
        aria-live="polite"
        sx={{
          maxWidth: 600,
          mx: 'auto',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexWrap: 'wrap',
          gap: 1.75,
          backgroundColor:
            phase === 'operational' ? marketingSurface.accentTint : marketingColor.surface,
          border: `1px solid ${
            phase === 'unreachable' ? marketingLine.error : marketingLine.hairlineStrong
          }`,
          borderRadius: '6px',
          px: 3,
          py: 2.5,
        }}
      >
        <Box
          aria-hidden
          sx={{
            width: 9,
            height: 9,
            borderRadius: '999px',
            backgroundColor: tone,
            boxShadow: phase === 'operational' ? `0 0 8px ${tone}` : 'none',
            ...(phase === 'operational'
              ? { animation: 'motzuBlink 2s ease-in-out infinite' }
              : null),
            '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
          }}
        />
        <Box sx={{ textAlign: 'left' }}>
          <Typography
            sx={{
              fontFamily: marketingFont.display,
              fontSize: 15,
              fontWeight: 700,
              color: tone,
            }}
          >
            {t(`public.contact.apiStatus.${phase}`)}
          </Typography>
          {checkedAt ? (
            <Typography
              sx={{ fontFamily: marketingFont.mono, fontSize: 11, color: marketingColor.textMuted }}
            >
              {t('public.contact.apiStatus.checkedAt', {
                time: checkedAt.toLocaleTimeString(),
              })}
            </Typography>
          ) : null}
        </Box>
      </Box>

      <Button
        variant="text"
        onClick={check}
        disabled={phase === 'checking'}
        sx={{
          mt: 2,
          fontFamily: marketingFont.mono,
          fontSize: 12,
          textTransform: 'uppercase',
          letterSpacing: '0.04em',
          color: marketingColor.textSecondary,
          '&:hover': { color: marketingColor.accent, backgroundColor: 'transparent' },
        }}
      >
        {t('public.contact.apiStatus.recheck')}
      </Button>
    </Box>
  );
};
